import type { ScheduleResponse, ScoresResponse } from '../../shared/types';

/**
 * Playoff odds by simulation.
 *
 * The shape of the problem is not the same as the win probabilities on the
 * Luck page. There, a single game is scored against a projection that already
 * exists. Here there are no projections for weeks that are months away, so the
 * inputs have to be a team's own scoring level and the spread around it, and
 * the output is a whole season played out thousands of times.
 *
 * THE FAILURE MODE THIS IS BUILT AGAINST is the one the expected-wins model
 * already hit once in this repo: a model that is arithmetically flawless and
 * describes nothing. Two specific guards follow from that.
 *
 * First, a team's scoring average over a handful of games is mostly noise. In
 * 2025 the within-team week-to-week spread was 24.2 points against a
 * between-team spread of true strength of only 10.2, so after three games a
 * team's own average carries barely a third of the signal. Every strength here
 * is therefore shrunk toward a prior, by an amount ESTIMATED FROM THE LEAGUE'S
 * OWN VARIANCE rather than chosen.
 *
 * Second, the answer is reported as a distribution, never as a verdict. A team
 * that misses in every simulation is shown as "<1%", not 0 -- the same clamp
 * the home page's projected matchups use, for the same reason.
 */

// --- Deterministic randomness ----------------------------------------------

/**
 * Seeded PRNG (mulberry32), used instead of `Math.random` deliberately.
 *
 * With an unseeded generator the page would print 61% on one render and 59% on
 * the next, purely from resampling. A reader cannot tell that apart from the
 * data having changed, and "the odds moved because you scrolled" is worse than
 * any sampling error. A fixed seed makes the whole page a pure function of the
 * league data, so it only ever moves when a game does.
 */
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** Box-Muller, returning one of the two normals it generates. */
function normal(rng: () => number, mean: number, sd: number): number {
  const u = Math.max(rng(), Number.EPSILON);
  const v = rng();
  return mean + sd * Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

// --- Strength -------------------------------------------------------------

export interface TeamStrength {
  rosterId: number;
  /** Games played this season. */
  games: number;
  /** This season's own scoring average, null before a game is played. */
  mean: number | null;
  /** What we expected before the season, expressed in this season's units. */
  prior: number;
  /** The figure the simulation actually uses. */
  strength: number;
  /**
   * How wrong `strength` could be, as a standard deviation in points per week.
   *
   * Not decoration -- the simulation samples this. A team plays every one of
   * its remaining games at whatever its true level is, so an error here is
   * fully correlated across the season and never averages out, unlike the
   * week-to-week noise. Leaving it out was measured to put a team at 90% to
   * make the playoffs before a single game had been played.
   */
  uncertainty: number;
  /** Share of `strength` coming from this season's games, 0..1. */
  weightOnSeason: number;
}

export interface StrengthModel {
  teams: Map<number, TeamStrength>;
  /** Points per team per week, league-wide. */
  leagueMean: number;
  /** Pooled within-team week-to-week standard deviation. */
  sigma: number;
  /**
   * Games of evidence at which a team's own average and the prior carry equal
   * weight. Estimated as within-team variance over between-team variance.
   */
  shrinkage: number;
  /** Fraction of last season's edge assumed to persist into this one. */
  carryover: number;
  /** True when `carryover` was measured rather than assumed. */
  carryoverMeasured: boolean;
  /** Where the variance estimates came from. */
  basis: 'season' | 'prior' | 'default';
  /** True when no season, past or present, supplied a scoring level. */
  flat: boolean;
}

/**
 * Fallbacks for a league with nothing on record at all.
 *
 * Only reachable in an inaugural preseason. The values are this league's own
 * 2025 measurements rather than invented round numbers, and the moment a single
 * week is played they stop being used.
 */
const DEFAULT_SIGMA = 24.2;
const DEFAULT_SHRINKAGE = 5.6;
const DEFAULT_LEAGUE_MEAN = 130;

/**
 * How much of a manager's scoring edge is assumed to survive into next season,
 * used only until the league has enough season-to-season pairs to measure it.
 *
 * A dynasty league keeps most of its rosters intact year to year, so the true
 * value is probably well above half; it is set at half deliberately, because
 * the cost of the two errors is not symmetric. Too low makes preseason odds
 * bland and they correct within a month of real games. Too high states last
 * year's result as this year's forecast, which is the exact thing a reader
 * would be right to call the page out for.
 */
const ASSUMED_CARRYOVER = 0.5;

/** Season-to-season observations required before carryover is measured. */
const MIN_CARRYOVER_OBSERVATIONS = 20;

interface Sample {
  games: number;
  total: number;
  sumSq: number;
}

const blankSample = (): Sample => ({ games: 0, total: 0, sumSq: 0 });

function addSample(s: Sample, points: number): void {
  s.games += 1;
  s.total += points;
  s.sumSq += points * points;
}

const sampleMean = (s: Sample): number | null => (s.games === 0 ? null : s.total / s.games);

function sampleVariance(s: Sample): number | null {
  if (s.games < 2) return null;
  const m = s.total / s.games;
  return Math.max(0, s.sumSq / s.games - m * m);
}

/** Per-roster scores from the fixture list's completed games. */
function scoresByRoster(schedule: ScheduleResponse | null): Map<number, Sample> {
  const out = new Map<number, Sample>();
  const get = (id: number) => {
    const s = out.get(id) ?? blankSample();
    out.set(id, s);
    return s;
  };
  for (const id of Object.keys(schedule?.rosterOwners ?? {})) get(Number(id));
  for (const g of schedule?.games ?? []) {
    if (g.pointsA === null || g.pointsB === null) continue;
    addSample(get(g.rosterA), g.pointsA);
    addSample(get(g.rosterB), g.pointsB);
  }
  return out;
}

/** Per-manager scores from a completed season's game list. */
function scoresByUser(season: ScoresResponse): Map<string, Sample> {
  const out = new Map<string, Sample>();
  const get = (id: string) => {
    const s = out.get(id) ?? blankSample();
    out.set(id, s);
    return s;
  };
  for (const g of season.games) {
    // Regular season only. A playoff field is a biased sample -- the weakest
    // teams stop appearing exactly when the strongest play every week.
    if (g.isPlayoff) continue;
    const a = season.rosterOwners[String(g.rosterA)];
    const b = season.rosterOwners[String(g.rosterB)];
    if (a) addSample(get(a), g.pointsA);
    if (b) addSample(get(b), g.pointsB);
  }
  return out;
}

interface VarianceEstimate {
  sigma: number;
  shrinkage: number;
}

/**
 * Splits observed spread into the part that is real and the part that is noise.
 *
 * The observed variance of team averages overstates how different teams truly
 * are, because each average carries its own sampling error of `within / n`.
 * Subtracting that leaves the between-team variance worth shrinking toward, and
 * their ratio is the number of games at which a team's own record and the prior
 * deserve equal weight. Measured on 2025: within-team sd 24.2, true between-team
 * sd 10.2, so the crossover sits at about 5.6 games.
 */
function estimateVariance(samples: Sample[]): VarianceEstimate | null {
  const usable = samples.filter((s) => s.games >= 2);
  if (usable.length < 2) return null;

  const withinVar =
    usable.reduce((t, s) => t + (sampleVariance(s) ?? 0), 0) / usable.length;
  const means = usable.map((s) => s.total / s.games);
  const grand = means.reduce((a, b) => a + b, 0) / means.length;
  const observedBetween =
    means.reduce((t, m) => t + (m - grand) * (m - grand), 0) / means.length;
  const meanGames = usable.reduce((t, s) => t + s.games, 0) / usable.length;

  const trueBetween = observedBetween - withinVar / meanGames;
  if (withinVar <= 0) return null;

  // A non-positive estimate means the teams are indistinguishable at this
  // sample size; clamping the ratio rather than dividing by ~0 keeps the model
  // at "shrink almost everything" instead of producing an infinity.
  const shrinkage =
    trueBetween <= 0 ? 40 : Math.min(40, Math.max(1, withinVar / trueBetween));

  return { sigma: Math.sqrt(withinVar), shrinkage };
}

/**
 * How much of a manager's scoring edge carried from one season to the next,
 * regressed through the origin across every consecutive pair on record.
 *
 * Through the origin because the quantity regressed is a deviation from the
 * league mean in each season, and a league's deviations sum to zero by
 * construction, so the intercept is zero by construction too -- the same
 * argument `fitMargins` makes about margins.
 *
 * Returns null until there is enough to measure, which with ten managers means
 * two completed transitions. One transition is ten points and would swing hard
 * on a single manager having a good year.
 */
export function fitCarryover(seasons: ScoresResponse[]): number | null {
  const ordered = [...seasons]
    .filter((s) => s.games.some((g) => !g.isPlayoff))
    .sort((a, b) => Number(a.season) - Number(b.season));
  if (ordered.length < 2) return null;

  const deviations = ordered.map((season) => {
    const byUser = scoresByUser(season);
    const means = [...byUser.entries()]
      .map(([user, s]) => ({ user, mean: sampleMean(s) }))
      .filter((r): r is { user: string; mean: number } => r.mean !== null);
    const grand = means.reduce((t, r) => t + r.mean, 0) / Math.max(1, means.length);
    return new Map(means.map((r) => [r.user, r.mean - grand]));
  });

  let sxy = 0;
  let sxx = 0;
  let n = 0;
  for (let i = 1; i < deviations.length; i += 1) {
    for (const [user, prev] of deviations[i - 1]) {
      const next = deviations[i].get(user);
      if (next === undefined) continue; // manager left the league
      sxy += prev * next;
      sxx += prev * prev;
      n += 1;
    }
  }

  if (n < MIN_CARRYOVER_OBSERVATIONS || sxx === 0) return null;
  // Clamped: a negative slope would mean last year's best are this year's
  // worst, which no plausible sample size here could establish.
  return Math.min(1, Math.max(0, sxy / sxx));
}

/**
 * Builds the per-team scoring level the simulation samples from.
 *
 * `priorSeasons` should be every COMPLETED season's scores, oldest first; the
 * most recent supplies each manager's carried-over edge and, before a game has
 * been played this year, the variance estimates too.
 */
export function buildStrength(
  schedule: ScheduleResponse | null,
  priorSeasons: ScoresResponse[] = [],
): StrengthModel {
  const current = scoresByRoster(schedule);
  const currentSamples = [...current.values()];
  const playedGames = currentSamples.reduce((t, s) => t + s.games, 0);

  const prior = [...priorSeasons]
    .filter((s) => s.games.some((g) => !g.isPlayoff))
    .sort((a, b) => Number(a.season) - Number(b.season));
  const lastSeason = prior[prior.length - 1] ?? null;
  const lastByUser = lastSeason ? scoresByUser(lastSeason) : null;

  // Variance comes from this season once it can support an estimate, and from
  // the most recent completed season until then.
  const fromSeason = estimateVariance(currentSamples);
  const fromPrior = lastByUser ? estimateVariance([...lastByUser.values()]) : null;
  const estimate = fromSeason ?? fromPrior;
  const basis: StrengthModel['basis'] = fromSeason ? 'season' : fromPrior ? 'prior' : 'default';

  const sigma = estimate?.sigma ?? DEFAULT_SIGMA;
  const shrinkage = estimate?.shrinkage ?? DEFAULT_SHRINKAGE;

  const measured = fitCarryover(prior);
  const carryover = measured ?? ASSUMED_CARRYOVER;

  const currentMeans = currentSamples
    .map((s) => sampleMean(s))
    .filter((m): m is number => m !== null);
  const priorMeans = lastByUser
    ? [...lastByUser.values()].map((s) => sampleMean(s)).filter((m): m is number => m !== null)
    : [];

  const priorLeagueMean =
    priorMeans.length > 0 ? priorMeans.reduce((a, b) => a + b, 0) / priorMeans.length : null;
  const leagueMean =
    currentMeans.length > 0
      ? currentMeans.reduce((a, b) => a + b, 0) / currentMeans.length
      : (priorLeagueMean ?? DEFAULT_LEAGUE_MEAN);

  const flat = currentMeans.length === 0 && priorLeagueMean === null;

  // How much of last season's raw scoring average was signal rather than
  // sampling noise. Carrying the RAW deviation forward would import a full
  // season's worth of luck into this season's prior: 2025's team averages
  // spread 12.1 points, of which only about 10.2 was real.
  const priorShrink = lastByUser
    ? (() => {
        const est = estimateVariance([...lastByUser.values()]);
        const games =
          [...lastByUser.values()].reduce((t, s) => t + s.games, 0) /
          Math.max(1, lastByUser.size);
        return est ? games / (games + est.shrinkage) : 1;
      })()
    : 1;

  const teams = new Map<number, TeamStrength>();
  for (const [rosterId, sample] of current) {
    const userId = schedule?.rosterOwners[String(rosterId)] ?? null;
    const lastMean = userId && lastByUser ? sampleMean(lastByUser.get(userId) ?? blankSample()) : null;

    // A manager with no history sits at the league mean rather than inheriting
    // the roster's previous occupant, whose team this no longer is.
    const priorLevel =
      lastMean !== null && priorLeagueMean !== null
        ? leagueMean + carryover * priorShrink * (lastMean - priorLeagueMean)
        : leagueMean;

    const mean = sampleMean(sample);
    const n = sample.games;
    const weight = n === 0 ? 0 : n / (n + shrinkage);
    const strength = mean === null ? priorLevel : weight * mean + (1 - weight) * priorLevel;

    // Posterior spread for a normal mean with a normal prior: sigma over the
    // root of (observations + prior weight). With nothing played it collapses
    // to the full between-team spread, which is the honest statement of how
    // little a fixture list on its own can say.
    const uncertainty = sigma / Math.sqrt(n + shrinkage);

    teams.set(rosterId, {
      rosterId,
      games: n,
      mean: mean === null ? null : Math.round(mean * 100) / 100,
      prior: Math.round(priorLevel * 100) / 100,
      strength: Math.round(strength * 100) / 100,
      uncertainty: Math.round(uncertainty * 100) / 100,
      weightOnSeason: Math.round(weight * 1000) / 1000,
    });
  }

  return {
    teams,
    leagueMean: Math.round(leagueMean * 100) / 100,
    sigma: Math.round(sigma * 100) / 100,
    shrinkage: Math.round(shrinkage * 10) / 10,
    carryover: Math.round(carryover * 100) / 100,
    carryoverMeasured: measured !== null,
    basis,
    flat: flat && playedGames === 0,
  };
}

// --- Bracket ---------------------------------------------------------------

/**
 * Seed pairing order for a single-elimination bracket, e.g. [1,8,4,5,2,7,3,6].
 *
 * Verified against this league's 2025 winners bracket rather than assumed:
 * with six qualifiers in an eight-slot bracket, seeds 7 and 8 do not exist, so
 * seeds 1 and 2 draw byes and the second round is 1 v winner(4/5) and
 * 2 v winner(3/6). That is exactly what Sleeper produced -- the bracket is
 * FIXED, not reseeded after the first round, which matters because reseeding
 * would send the top seed the weakest survivor every time.
 */
export function bracketOrder(slots: number): number[] {
  let order = [1];
  while (order.length < slots) {
    const size = order.length * 2;
    const next: number[] = [];
    for (const seed of order) next.push(seed, size + 1 - seed);
    order = next;
  }
  return order;
}

/** Bracket slots a field of `playoffTeams` occupies, and the byes it implies. */
export function bracketShape(playoffTeams: number): { slots: number; byes: number } {
  const slots = 2 ** Math.ceil(Math.log2(Math.max(2, playoffTeams)));
  return { slots, byes: slots - playoffTeams };
}

// --- Simulation ------------------------------------------------------------

export interface OddsRow {
  rosterId: number;
  /** Wins, counting a tie as half, averaged over the simulations. */
  meanWins: number;
  meanPointsFor: number;
  playoff: number;
  bye: number;
  title: number;
  meanSeed: number;
  /** Share of simulations finishing in each seed, index 0 = first. */
  seedShare: number[];
  /**
   * Made the playoffs in every simulation, which is NOT the same as having
   * clinched. Ten thousand runs missing an outcome only bounds it below about
   * 1 in 10,000; `clinch` is the arithmetic test.
   */
  alwaysIn: boolean;
  /** Missed in every simulation. Same caveat as `alwaysIn`. */
  alwaysOut: boolean;
  /**
   * Settled by arithmetic rather than by sampling: 'in' when the team cannot be
   * caught, 'out' when it cannot catch up, null while it is still live.
   */
  clinch: ClinchState;
}

export type ClinchState = 'in' | 'out' | null;

/**
 * Who is mathematically settled, decided on wins alone.
 *
 * Deliberately conservative. Sleeper breaks ties on points-for, and a team can
 * certainly be locked in on a tiebreak it cannot lose -- but proving that needs
 * the maximum points every rival could still score, which is unbounded. Testing
 * only strict win counts can therefore report "still live" for a team that has
 * in fact clinched, and never the reverse. Announcing a berth that later
 * evaporates is the error that matters; being a week late is not.
 */
export function clinchStates(
  schedule: ScheduleResponse | null,
  playoffTeams: number,
): Map<number, ClinchState> {
  const wins = new Map<number, number>();
  const left = new Map<number, number>();
  for (const id of Object.keys(schedule?.rosterOwners ?? {})) {
    wins.set(Number(id), 0);
    left.set(Number(id), 0);
  }

  for (const g of schedule?.games ?? []) {
    if (g.pointsA === null || g.pointsB === null) {
      left.set(g.rosterA, (left.get(g.rosterA) ?? 0) + 1);
      left.set(g.rosterB, (left.get(g.rosterB) ?? 0) + 1);
      continue;
    }
    if (g.pointsA > g.pointsB) wins.set(g.rosterA, (wins.get(g.rosterA) ?? 0) + 1);
    else if (g.pointsB > g.pointsA) wins.set(g.rosterB, (wins.get(g.rosterB) ?? 0) + 1);
    else {
      wins.set(g.rosterA, (wins.get(g.rosterA) ?? 0) + 0.5);
      wins.set(g.rosterB, (wins.get(g.rosterB) ?? 0) + 0.5);
    }
  }

  const ids = [...wins.keys()];
  const out = new Map<number, ClinchState>();
  const ceiling = (id: number) => (wins.get(id) ?? 0) + (left.get(id) ?? 0);

  for (const me of ids) {
    // Rivals who finish above me no matter what either of us does.
    const certainlyAhead = ids.filter((other) => other !== me && (wins.get(other) ?? 0) > ceiling(me));
    // Rivals I finish above no matter what.
    const certainlyBehind = ids.filter(
      (other) => other !== me && (wins.get(me) ?? 0) > ceiling(other),
    );

    if (certainlyAhead.length >= playoffTeams) out.set(me, 'out');
    else if (certainlyBehind.length >= ids.length - playoffTeams) out.set(me, 'in');
    else out.set(me, null);
  }

  return out;
}

/**
 * How much one upcoming game moves a team's own playoff odds.
 *
 * Conditioned inside the same simulation run rather than by re-simulating:
 * every run already records who won this game and who made the playoffs, so
 * splitting the runs by that game's outcome gives the two conditional
 * probabilities exactly, at no extra cost.
 */
export interface SwingRow {
  week: number;
  matchupId: number;
  rosterA: number;
  rosterB: number;
  aIfWin: number;
  aIfLose: number;
  bIfWin: number;
  bIfLose: number;
  /** The larger of the two teams' swings, for ranking. */
  swing: number;
}

export interface Simulation {
  sims: number;
  rows: OddsRow[];
  swings: SwingRow[];
  swingWeek: number | null;
  model: StrengthModel;
  playoffTeams: number;
  byes: number;
  gamesPlayed: number;
  gamesRemaining: number;
}

const SIMS = 10_000;
const SEED = 0x5eed;

/**
 * Plays the rest of the regular season, and then the bracket, many times over.
 *
 * Seeding follows Sleeper's own rule -- wins first, points-for as the tiebreak
 * -- which is also what the standings page sorts on, so a reader comparing the
 * two pages sees the same order.
 */
export function simulateSeason(
  schedule: ScheduleResponse | null,
  playoffTeams: number,
  model: StrengthModel,
  sims: number = SIMS,
): Simulation {
  const rosterIds = Object.keys(schedule?.rosterOwners ?? {})
    .map(Number)
    .sort((a, b) => a - b);
  const index = new Map(rosterIds.map((id, i) => [id, i]));
  const n = rosterIds.length;

  const { slots, byes } = bracketShape(Math.min(playoffTeams, n));
  const qualifiers = Math.min(playoffTeams, n);

  // Results already in the books, which every simulation starts from.
  const baseWins = new Float64Array(n);
  const basePoints = new Float64Array(n);
  const pending: Array<{ week: number; matchupId: number; a: number; b: number }> = [];
  let gamesPlayed = 0;

  for (const g of schedule?.games ?? []) {
    const a = index.get(g.rosterA);
    const b = index.get(g.rosterB);
    if (a === undefined || b === undefined) continue;
    if (g.pointsA === null || g.pointsB === null) {
      pending.push({ week: g.week, matchupId: g.matchupId, a, b });
      continue;
    }
    gamesPlayed += 1;
    basePoints[a] += g.pointsA;
    basePoints[b] += g.pointsB;
    if (g.pointsA > g.pointsB) baseWins[a] += 1;
    else if (g.pointsB > g.pointsA) baseWins[b] += 1;
    else {
      baseWins[a] += 0.5;
      baseWins[b] += 0.5;
    }
  }

  const strength = new Float64Array(n);
  const uncertainty = new Float64Array(n);
  for (let i = 0; i < n; i += 1) {
    const team = model.teams.get(rosterIds[i]);
    strength[i] = team?.strength ?? model.leagueMean;
    uncertainty[i] = team?.uncertainty ?? 0;
  }
  const sigma = Math.max(1, model.sigma);

  // Only the next week's games get a swing figure. Further out, the answer is
  // dominated by everything else that happens in between and stops being about
  // that game at all.
  const swingWeek = pending.length > 0 ? Math.min(...pending.map((g) => g.week)) : null;
  const swingGames = pending.filter((g) => g.week === swingWeek);
  // pending index -> swing index, so the sampling loop can record the outcome
  // of a game the conditional counters care about without a second pass.
  const swingSlot = new Int32Array(pending.length).fill(-1);
  for (let g = 0; g < pending.length; g += 1) {
    const at = swingGames.indexOf(pending[g]);
    if (at !== -1) swingSlot[g] = at;
  }
  const swingAWon = new Uint8Array(swingGames.length);
  const swingCounts = swingGames.map(() => ({
    aWins: 0,
    aWinAIn: 0,
    aWinBIn: 0,
    aLossAIn: 0,
    aLossBIn: 0,
  }));

  const playoffCount = new Float64Array(n);
  const byeCount = new Float64Array(n);
  const titleCount = new Float64Array(n);
  const winSum = new Float64Array(n);
  const pointSum = new Float64Array(n);
  const seedCount: Float64Array[] = rosterIds.map(() => new Float64Array(n));

  const rng = mulberry32(SEED);
  const wins = new Float64Array(n);
  const points = new Float64Array(n);
  const madePlayoffs = new Uint8Array(n);
  const bracketSeeds = bracketOrder(slots);
  const order = Array.from({ length: n }, (_, i) => i);
  const level = new Float64Array(n);

  for (let s = 0; s < sims; s += 1) {
    wins.set(baseWins);
    points.set(basePoints);

    // One draw of "how good is this team really", held fixed for the whole
    // simulated season. Re-drawing it per game would quietly average the
    // uncertainty away and reproduce the over-confident model this replaced:
    // the error is the same every week, which is exactly why it matters.
    for (let i = 0; i < n; i += 1) {
      level[i] = uncertainty[i] > 0 ? normal(rng, strength[i], uncertainty[i]) : strength[i];
    }

    for (let g = 0; g < pending.length; g += 1) {
      const { a, b } = pending[g];
      const pa = normal(rng, level[a], sigma);
      const pb = normal(rng, level[b], sigma);
      points[a] += pa;
      points[b] += pb;
      const aWon = pa > pb;
      if (aWon) wins[a] += 1;
      else wins[b] += 1;
      const slot = swingSlot[g];
      if (slot !== -1) swingAWon[slot] = aWon ? 1 : 0;
    }

    // Sleeper's own standings rule: record, then points for.
    const sorted = order.slice().sort((x, y) => wins[y] - wins[x] || points[y] - points[x]);

    madePlayoffs.fill(0);
    for (let seat = 0; seat < n; seat += 1) {
      const team = sorted[seat];
      seedCount[team][seat] += 1;
      if (seat < qualifiers) {
        playoffCount[team] += 1;
        madePlayoffs[team] = 1;
      }
      if (seat < byes) byeCount[team] += 1;
      winSum[team] += wins[team];
      pointSum[team] += points[team];
    }

    // --- Bracket. Slot -> team index, with absent seeds left empty.
    let alive: Array<number | null> = bracketSeeds.map((seed) =>
      seed <= qualifiers ? sorted[seed - 1] : null,
    );
    while (alive.length > 1) {
      const next: Array<number | null> = [];
      for (let i = 0; i < alive.length; i += 2) {
        const x = alive[i];
        const y = alive[i + 1];
        if (x === null) next.push(y);
        else if (y === null) next.push(x);
        else {
          const px = normal(rng, level[x], sigma);
          const py = normal(rng, level[y], sigma);
          next.push(px > py ? x : y);
        }
      }
      alive = next;
    }
    const champion = alive[0];
    if (champion !== null && champion !== undefined) titleCount[champion] += 1;

    for (let g = 0; g < swingGames.length; g += 1) {
      const { a, b } = swingGames[g];
      const c = swingCounts[g];
      const aIn = madePlayoffs[a] === 1;
      const bIn = madePlayoffs[b] === 1;
      if (swingAWon[g] === 1) {
        c.aWins += 1;
        if (aIn) c.aWinAIn += 1;
        if (bIn) c.aWinBIn += 1;
      } else {
        if (aIn) c.aLossAIn += 1;
        if (bIn) c.aLossBIn += 1;
      }
    }
  }

  const clinch = clinchStates(schedule, qualifiers);

  const rows: OddsRow[] = rosterIds.map((rosterId, i) => {
    const seedShare = Array.from(seedCount[i], (c) => c / sims);
    return {
      rosterId,
      meanWins: winSum[i] / sims,
      meanPointsFor: pointSum[i] / sims,
      playoff: playoffCount[i] / sims,
      bye: byeCount[i] / sims,
      title: titleCount[i] / sims,
      meanSeed: seedShare.reduce((t, share, seat) => t + share * (seat + 1), 0),
      seedShare,
      alwaysIn: playoffCount[i] === sims,
      alwaysOut: playoffCount[i] === 0,
      clinch: clinch.get(rosterId) ?? null,
    };
  });

  rows.sort((a, b) => b.playoff - a.playoff || b.meanWins - a.meanWins);

  const swings: SwingRow[] = swingGames.map((g, i) => {
    const c = swingCounts[i];
    const aLosses = sims - c.aWins;
    // A side that wins (or loses) this game in none of the simulations has no
    // conditional to report. That is not a rounding artefact -- it happens once
    // a team is mathematically settled -- so the branch is left undefined
    // rather than being shown as 0%.
    const share = (hits: number, of: number) => (of === 0 ? Number.NaN : hits / of);
    const aIfWin = share(c.aWinAIn, c.aWins);
    const aIfLose = share(c.aLossAIn, aLosses);
    const bIfWin = share(c.aLossBIn, aLosses);
    const bIfLose = share(c.aWinBIn, c.aWins);
    const spread = (x: number, y: number) =>
      Number.isNaN(x) || Number.isNaN(y) ? 0 : Math.abs(x - y);

    return {
      week: g.week,
      matchupId: g.matchupId,
      rosterA: rosterIds[g.a],
      rosterB: rosterIds[g.b],
      aIfWin,
      aIfLose,
      bIfWin,
      bIfLose,
      swing: Math.max(spread(aIfWin, aIfLose), spread(bIfWin, bIfLose)),
    };
  });

  swings.sort((a, b) => b.swing - a.swing);

  return {
    sims,
    rows,
    swings,
    swingWeek,
    model,
    playoffTeams: qualifiers,
    byes,
    gamesPlayed,
    gamesRemaining: pending.length,
  };
}
